import {
  helveticaLight,
  helveticaRegular,
} from '@/app/fonts';
import { fluidBreakpoints, medias } from '@/utils/sizes';
import { generateClampFromMax, generateClampFromMinMax } from '@/utils/styling';
import { createGlobalStyle, css } from 'styled-components';

export const darkThemeVars = css`
  --c-font-primary: var(--c-white);
  --c-font-secondary: var(--c-white-600);
  --c-font-tertiary: var(--c-white-400);
  --c-border: var(--c-white-400);
  --bg-page: var(--bg-black);
`;

export const obsoleteThemeVars = css`
  --bg-page: var(--bg-obsolete);
`;

export const GlobalStyle = createGlobalStyle`
  /* ${helveticaRegular.variable};
  ${helveticaLight.variable};*/

  html {
    scrollbar-gutter: stable;

    /* TYPOGRAPHY */
    --ff-helvetica-regular: 'helveticaRegular';
    --ff-helvetica-light: 'helveticaLight';
    /* Ponemos helvetica de momento, ya que la gt america no tiene acentos ni caracteres especiales */
    --ff-gt-america-regular: 'helveticaRegular';

    font-family: var(--font-helvetica-regular), sans-serif;

    /* 
      All sizes relative to this fluid size for fluid sizes max and min breakpoints 
      Start fluid effect at 1280px (from 1024px to 1280px there are no screens, so being not proportional is not so important)
    */
    font-size: ${generateClampFromMinMax(12, 21.35, fluidBreakpoints.max, 1280)};

    --fs-5xl: 5.61rem;
    --fs-4xl: 4.209rem;
    --fs-3xl: 3.157rem;
    --fs-2xl: 2.369rem;
    --fs-xl: 1.777rem;
    --fs-lg: 1.333rem;
    --fs-base: 1rem;
    --fs-sm: 0.75rem;
    --fs-xs: 0.563rem;

    ${medias.max.tablet} {
      --fs-2xl: 1.9rem;
    }

    ${medias.max.mobile} {
      --fs-4xl: clamp(16px, 7vw, 35px);
      --fs-3xl: clamp(16px, 5.25vw, 28px);
      --fs-2xl: 16px;
    }

    /* Font sizes by tag */
    --fs-h1: var(--fs-5xl);
    --fs-h2: var(--fs-4xl);
    --fs-h3: var(--fs-3xl);
    --fs-h4: var(--fs-2xl);
    --fs-h5: var(--fs-xl);
    --fs-h6: var(--fs-lg);
    --fs-p: var(--fs-base);

    /* Line heights */
    --lh-lg: 1.4;
    --lh-base: 1.2;
    --lh-sm: 1.1;

    /* COLORS */
    --c-white: rgb(237, 237, 234);
    --c-white-600: rgb(169, 169, 167);
    --c-white-400: rgb(136, 136, 134);
    --c-white-200: rgb(102, 102, 101);

    --c-black: rgb(16, 16, 15);
    --c-black-600: rgb(159, 159, 159);
    --c-black-400: rgb(47, 47, 47);
    --c-black-200: rgb(58, 58, 57);

    --bg-main: rgb(237, 237, 234);
    --bg-obsolete: #d1d6d6;
    --bg-black: var(--c-black);
    --bg-transparent: transparent;
    --bg-page: var(--bg-main);

    --c-font-primary: var(--c-black);
    --c-font-secondary: var(--c-black-600);
    --c-font-tertiary: #70706f;
    --c-font-white: var(--c-white);

    --c-border: rgb(193, 193, 188);

    background-color: var(--bg-page);
    transition: background-color var(--tr-base);

    /* SIZES */
    --max-width-full: 100%;
    --max-width-content: 2010px;
    --max-width-main: 2420px;

    /* 
      SPACING 
      max size is set and min size is calculated based on the fluid width ratio
    */
    --spacing-component-xl: ${generateClampFromMax(285)};
    --spacing-component-lg: ${generateClampFromMax(225)};
    --spacing-component: ${generateClampFromMax(210)};
    --spacing-component-sm: ${generateClampFromMax(150)};



    --spacing-component-inner-2xl: ${generateClampFromMinMax(65, 125)};
    --spacing-component-inner-xl: ${generateClampFromMinMax(56, 100)};
    --spacing-component-inner-lg: ${generateClampFromMinMax(40, 80)};
    --spacing-component-inner: ${generateClampFromMinMax(17, 45)};
    --spacing-component-inner-half: ${generateClampFromMinMax(7.5, 20)};
    --spacing-component-inner-sm: ${generateClampFromMinMax(12, 25)};
    --spacing-component-inner-xs: ${generateClampFromMinMax(8, 15)};

    // From --spacing-componen minus --spacing-component-inner
    --separator-height-base: ${generateClampFromMinMax(15, 200 - 40)};

    --mg-top-page-lg: ${generateClampFromMinMax(100, 300)};
    --mg-top-page-base: ${generateClampFromMinMax(80, 170)};
    --mg-top-page-sm: ${generateClampFromMinMax(60, 100)};

    ${medias.max.tablet} {
        --spacing-component: clamp(20px, 10vw, 150px); // 60px aprox for 420px viewport width
        --spacing-component-xl: clamp(20px, 14vw, 150px);
        --spacing-component-lg: clamp(20px, 14vw, 150px);
        --page-pd-mobile-tablet: 10vw;
        --spacing-inner-vertical-mobile-tablet: 35px;
        --separator-height-base: 50px;

        font-size: 12px;
    }

    ${`${medias.max.tablet} and (orientation: landscape)`} {
      --spacing-component: clamp(20px, 5vw, 100px);

    }

    @media (max-width: 850px) {
      --page-pd-mobile-tablet: 6vw;
    }

    ${medias.max.mobile} {
      --spacing-component-lg: var(--separator-height-base);
      --spacing-component: var(--separator-height-base);
      --page-pd-mobile-tablet: 16px;
    }

    /* TRANSITIONS */
    --tr-fast: 0.2s;
    --tr-base: 0.3s;
    --tr-slow: 0.5s;

    @media (prefers-reduced-motion: reduce) {
      html {
        animation: none !important;
        transition: none !important;
        --tr-fast: 0;
        --tr-base: 0;
        --tr-slow: 0;
      }
    }

    ${medias.min.tablet} {
      .hidden-tablet-desktop {
        display: none;
      }
    }

    .display-only-mobile,
    *[data-mobile] {
      display: none;
    }

    ${medias.only.mobile} {
      .hidden-mobile {
        display: none;
      }

      .display-only-mobile,
      *[data-mobile] {
        display: flex;
      }
    }
  }

  html,
  body {
    scrollbar-gutter: stable;
    max-width: 100vw;
    overflow-x: clip;
    padding: 0;
    margin: 0;
    font-family: var(--ff-helvetica-regular);
  }

  html {
    box-sizing: border-box;
    padding: 0;
    margin: 0;

    a {
      text-decoration: none;
    }

    a, button {
      outline: none;
      -webkit-tap-highlight-color: transparent;
      -webkit-appearance: none;
    }

    ul {
      all: unset;
    }
  }

  html[data-theme='dark'],
  *[data-theme='dark'] {
    ${darkThemeVars};
  }

  html[data-theme='obsolete'],
  *[data-theme='obsolete'] {
    ${obsoleteThemeVars};
  }

  html:has(main[data-theme='dark']) {
    ${darkThemeVars};

    header .logo-container-link img,
    svg {
      filter: invert(1);
    }

    footer .logo-container-link img {
      filter: invert(0);
    }

    .inverted-in-darkmode {
      filter: invert(1);
    }
  }

  html:has(main[data-theme='obsolete']) {
    ${obsoleteThemeVars};
  }

  .pswp__zoom-wrap {
    height: 100%;
    width: 100%;
    position: relative;
  }

  .pswp img {
    max-width: 0 !important;
    object-fit: contain !important;
    width: auto !important;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  #smooth-content,
  #smooth-wrapper {
    transform-style: preserve-3d;
    backface-visibility: hidden;
    will-change: transform;
  }

  img[data-events='false'] {
    pointer-events: none !important;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
    -webkit-user-drag: none;
  }
`;
